"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LogicalBusAssertions = exports.Rs485DependentSignals = exports.EthernetDependentSignals = exports.UsbDependentSignals = exports.SpiNoChipSelectContention = exports.SpiChipSelectDriven = exports.SpiSingleController = exports.I2cNoSharedAddress = exports.I2cAddressResolvable = exports.I2cLinesPulledUp = exports.I2cHasController = exports.DevicesConfigured = exports.AssignmentsValid = exports.RequiredSignalsAssigned = exports.LogicalBusRequirement = void 0;
const enums_1 = require("../enums");
const LOGICAL_BUS_REQUIREMENT_ID = 'req_cadstrombus0000000000000000000000000';
exports.LogicalBusRequirement = {
    id: LOGICAL_BUS_REQUIREMENT_ID,
    slug: 'cadstrom.bus',
    slugPrefix: 'BUS',
    name: 'Logical Bus',
    description: 'Asserts that a recognized bus interface — a set of nets and the device functions assigned to them (I²C, SPI, UART, USB, HDMI, Ethernet, …) — is correctly and completely wired: every participating device assigns the bus’s required signals, the assignments and operating states are valid, and protocol-specific rules (controller configuration, addressing, selection, required bias components) are satisfied. Evaluated once per bus.',
};
exports.RequiredSignalsAssigned = {
    id: 'asrt_busreqsigassigned000000000000000000',
    slug: 'cadstrom.bus.common.required_signals_assigned',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'Every required bus signal must be assigned',
    description: 'Every member of the bus must assign a component signal to each required signal of its bus function. A member that leaves a required bus signal unassigned cannot participate in the interface.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.AssignmentsValid = {
    id: 'asrt_busassignvalid000000000000000000000',
    slug: 'cadstrom.bus.common.assignments_valid',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'Bus signal assignments must be valid',
    description: 'Every assigned component signal must be a legal target for its bus signal; a signal that does not permit multiple assignments must have exactly one, and no signal may exceed its maximum number of assignments.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.DevicesConfigured = {
    id: 'asrt_busdevconfig00000000000000000000000',
    slug: 'cadstrom.bus.common.devices_configured',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'Bus devices must be properly configured to participate in the bus',
    description: 'Every member’s function state and mode state must be valid for it to act on the bus in the evaluated circuit state. A member held in a state in which its bus function is disabled cannot participate.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.I2cHasController = {
    id: 'asrt_busi2chascontroller0000000000000000',
    slug: 'cadstrom.bus.i2c.has_controller',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'An I²C bus must have at least one controller',
    description: 'An I²C bus must have a controller. I²C permits multiple controllers (multi-master), so any count of one or more is valid; a bus with only peripherals has nothing to initiate transfers.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.I2cLinesPulledUp = {
    id: 'asrt_busi2cpullups0000000000000000000000',
    slug: 'cadstrom.bus.i2c.lines_pulled_up',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'I²C SDA and SCL must be biased with pull-up resistors',
    description: 'The open-drain `SDA` and `SCL` lines must each have the pull-up resistor to a supply required to return them to their idle high state.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.I2cAddressResolvable = {
    id: 'asrt_busi2caddrresolvable000000000000000',
    slug: 'cadstrom.bus.i2c.address_resolvable',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'Every I²C peripheral must have a resolvable address',
    description: 'Each peripheral must expose the address information required to compute its bus address in the evaluated circuit state (fixed, selectable, or configurable). A peripheral whose address cannot be resolved cannot be reliably addressed.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.I2cNoSharedAddress = {
    id: 'asrt_busi2cnosharedaddr00000000000000000',
    slug: 'cadstrom.bus.i2c.no_shared_address',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'No two I²C devices may share the same address',
    description: 'A controller must not resolve two peripherals to the same address on the same bus lines. Two devices answering the same address collide and cannot be independently accessed.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.SpiSingleController = {
    id: 'asrt_busspisinglecontroller0000000000000',
    slug: 'cadstrom.bus.spi.single_controller',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'An SPI bus must have exactly one controller',
    description: 'SPI is a single-controller bus. A bus with zero controllers has nothing to initiate transfers; a bus with more than one controller has contending masters.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.SpiChipSelectDriven = {
    id: 'asrt_busspicsdriven000000000000000000000',
    slug: 'cadstrom.bus.spi.chip_select_driven',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'Every SPI peripheral must be selected by a controller-driven chip-select',
    description: 'Each peripheral’s chip-select line must be driven by a controller, not hardwired to a fixed level. A hardcoded select cannot be deasserted, so the peripheral cannot be multiplexed onto the bus.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.SpiNoChipSelectContention = {
    id: 'asrt_busspinocscontention000000000000000',
    slug: 'cadstrom.bus.spi.no_chip_select_contention',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'No two SPI peripherals may share the same chip-select selection',
    description: 'No two peripherals may be selected by the same chip-select state from a controller. Peripherals sharing an active selection contend when that select is asserted.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.UsbDependentSignals = {
    id: 'asrt_bususbdepsignals0000000000000000000',
    slug: 'cadstrom.bus.usb.dependent_signals_assigned',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'All dependent USB signals must be assigned together',
    description: 'A USB interface must assign its full co-required signal set — at minimum the `D+` / `D-` data pair (USB 3.x additionally requires the SuperSpeed `SSTX±` / `SSRX±` pairs). A partially-assigned data pair is an incomplete implementation.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.EthernetDependentSignals = {
    id: 'asrt_busethdepsignals0000000000000000000',
    slug: 'cadstrom.bus.ethernet.dependent_signals_assigned',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'All dependent Ethernet signals must be assigned together',
    description: 'A multi-pair Ethernet interface must assign its complete signal set — a 1000BASE-T link requires all four differential pairs `MDI0±`…`MDI3±`.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.Rs485DependentSignals = {
    id: 'asrt_busrs485depsignals00000000000000000',
    slug: 'cadstrom.bus.rs485.dependent_signals_assigned',
    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
    name: 'All dependent RS-485 signals must be assigned together',
    description: 'A full-duplex RS-485 interface must assign both co-required differential pairs — the driver pair (`Y`/`Z`) and the receiver pair (`A`/`B`).',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.LogicalBusAssertions = [
    exports.RequiredSignalsAssigned,
    exports.AssignmentsValid,
    exports.DevicesConfigured,
    exports.I2cHasController,
    exports.I2cLinesPulledUp,
    exports.I2cAddressResolvable,
    exports.I2cNoSharedAddress,
    exports.SpiSingleController,
    exports.SpiChipSelectDriven,
    exports.SpiNoChipSelectContention,
    exports.UsbDependentSignals,
    exports.EthernetDependentSignals,
    exports.Rs485DependentSignals,
];
//# sourceMappingURL=logical-bus.js.map